import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Snackbar,
  Alert
} from '@mui/material';
import { Search as SearchIcon, Add as AddIcon } from '@mui/icons-material';
import { authFetch } from '../../utils/api';

const ProductSelector = ({ open, onClose, onSelectProduct }) => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    if (open) {
      fetchProducts();
    }
  }, [open]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = products.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.hsn?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts(products);
    }
  }, [searchTerm, products]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await authFetch('/invoices/products');
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
        setFilteredProducts(data);
      } else {
        console.error('Failed to fetch products');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Normalize various unit spellings/cases from inventory to the standardized set used in invoice form
  const normalizeUnit = (unit) => {
    if (!unit) return 'PCS';
    const key = String(unit).trim().toLowerCase();
    const map = {
      pcs: 'PCS',
      nos: 'PCS',
      piece: 'PCS',
      pieces: 'PCS',
      cm: 'CM',
      mtr: 'MTR',
      meter: 'MTR',
      metre: 'MTR',
      inch: 'INCH',
      in: 'INCH',
      ft: 'FT',
      foot: 'FT',
      feet: 'FT',
      kg: 'KG',
      kilogram: 'KG',
      kilograms: 'KG',
      g: 'GMS',
      gram: 'GMS',
      grams: 'GMS',
      gms: 'GMS',
      ltr: 'LTR',
      liter: 'LTR',
      litre: 'LTR',
      ml: 'ML',
      box: 'BOX',
      carton: 'CARTON',
      packet: 'PACKET',
      bundle: 'BUNDLE',
      set: 'SET',
      pair: 'PAIR',
      dozen: 'DOZEN',
      hour: 'HOUR',
      day: 'DAY',
      month: 'MONTH',
      year: 'YEAR',
      sqm: 'SQM',
      sq_m: 'SQM',
      cum: 'CUM',
      ton: 'TON',
      quintal: 'QUINTAL',
    };
    return map[key] || String(unit).trim().toUpperCase();
  };

  const handleProductSelect = (product) => {
    if (!product.quantity || Number(product.quantity) <= 0) {
      setToastMessage('No stock available');
      setToastOpen(true);
      return;
    }

    const rate = Number(product.selling_price || product.rate || 0);
    const invoiceItem = {
      name: product.name,
      description: product.name, // Use name as description since it contains the real item name
      quantity: 1,
      unit: normalizeUnit(product.unit), // Normalize to match invoice unit options
      rate: rate,
      tax: product.gst_rate || 0, // Use GST rate from inventory
      discount: 0, // Default discount
      amount: rate, // Initial amount
      hsn: product.hsn || '', // Use HSN from inventory
      inventoryId: product.id, // Track inventory item ID for stock deduction
      currentStock: product.quantity, // Track current stock level
      stockStatus: product.status // Track stock status
    };

    console.log('Selected product with inventory details:', {
      inventoryId: product.id,
      currentStock: product.quantity,
      stockStatus: product.status
    });
    onSelectProduct(invoiceItem);
    onClose();
  };

  const handleAddSelected = () => {
    let hasOutOfStock = false;
    let addedCount = 0;

    selectedProducts.forEach(product => {
      if (!product.quantity || Number(product.quantity) <= 0) {
        hasOutOfStock = true;
        return;
      }
      addedCount++;
      const rate = Number(product.selling_price || product.rate || 0);
      const invoiceItem = {
        name: product.name,
        description: product.name, // Use name as description since it contains the real item name
        quantity: 1,
        unit: normalizeUnit(product.unit),
        rate: rate,
        tax: product.gst_rate || 0, // Use GST rate from inventory
        discount: 0,
        amount: rate,
        hsn: product.hsn || '', // Use HSN from inventory
        inventoryId: product.id, // Track inventory item ID for stock deduction
        currentStock: product.quantity, // Track current stock level
        stockStatus: product.status // Track stock status
      };
      onSelectProduct(invoiceItem);
    });
    
    if (hasOutOfStock) {
      setToastMessage(addedCount === 0 ? 'No stock available for selected items' : 'Some items were skipped due to no stock');
      setToastOpen(true);
    }
    
    setSelectedProducts([]);
    if (addedCount > 0) {
      onClose();
    }
  };

  const toggleProductSelection = (product) => {
    setSelectedProducts(prev => {
      const isSelected = prev.some(p => p.id === product.id);
      if (isSelected) {
        return prev.filter(p => p.id !== product.id);
      } else {
        return [...prev, product];
      }
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'low_stock': return 'warning';
      case 'out_of_stock': return 'error';
      default: return 'default';
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Select Products from Inventory</Typography>
          {selectedProducts.length > 0 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddSelected}
              color="primary"
            >
              Add Selected ({selectedProducts.length})
            </Button>
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box mb={2}>
          <TextField
            fullWidth
            placeholder="Search products by name, description, or HSN..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ mb: 2 }}
          />
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Select</TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>HSN</TableCell>
                  <TableCell align="center">Unit</TableCell>
                  <TableCell align="center">GST (%)</TableCell>
                  <TableCell align="right">Rate (₹)</TableCell>
                  <TableCell align="center">Stock</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Category</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts.map((product) => {
                  const isSelected = selectedProducts.some(p => p.id === product.id);
                  return (
                    <TableRow
                      key={product.id}
                      hover
                      onClick={() => toggleProductSelection(product)}
                      onDoubleClick={() => handleProductSelect(product)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleProductSelection(product)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {product.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {product.description || 'No description'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {product.hsn || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="medium">
                          {normalizeUnit(product.unit)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="medium">
                          {product.gst_rate || 0}%
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          ₹{Number(product.rate || 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {product.quantity}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={product.status}
                          color={getStatusColor(product.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {product.category || 'Uncategorized'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {filteredProducts.length === 0 && !loading && (
          <Box textAlign="center" p={3}>
            <Typography color="text.secondary">
              {searchTerm ? 'No products found matching your search.' : 'No products available in inventory.'}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => {
            let hasOutOfStock = false;
            let addedCount = 0;
            // Add all visible products
            filteredProducts.forEach(product => {
              if (!product.quantity || Number(product.quantity) <= 0) {
                hasOutOfStock = true;
                return;
              }
              addedCount++;
              const rate = Number(product.selling_price || product.rate || 0);
              const invoiceItem = {
                name: product.name,
                description: product.name, // Use name as description since it contains the real item name
                quantity: 1,
                unit: product.unit || 'PCS',
                rate: rate,
                tax: product.gst_rate || 0, // Use GST rate from inventory
                discount: 0,
                amount: rate,
                hsn: product.hsn || '', // Use HSN from inventory
                inventoryId: product.id, // Track inventory item ID for stock deduction
                currentStock: product.quantity, // Track current stock level
                stockStatus: product.status // Track stock status
              };
              onSelectProduct(invoiceItem);
            });
            
            if (hasOutOfStock) {
              setToastMessage(addedCount === 0 ? 'No stock available for visible items' : 'Some items were skipped due to no stock');
              setToastOpen(true);
            }
            
            if (addedCount > 0) {
              onClose();
            }
          }}
          disabled={filteredProducts.length === 0}
        >
          Add All Visible
        </Button>
      </DialogActions>
    </Dialog>
    <Snackbar open={toastOpen} autoHideDuration={3000} onClose={() => setToastOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
      <Alert onClose={() => setToastOpen(false)} severity="error" sx={{ width: '100%' }}>
        {toastMessage}
      </Alert>
    </Snackbar>
    </>
  );
};

export default ProductSelector;
